import { Injectable } from '@angular/core';
import { MapService } from './map.service';
import { IVehicleAddress } from '../interfaces/task-info';
import { VehicleAddressService } from './vehicle-address.service';
import { Subject } from 'rxjs';
import { QuoteAddressDTO } from '../interfaces/quote-address';

enum PcaFieldMode {
  /** Default of search and populate */
  DEFAULT = 3,
  /** The field will be ignored. */
  NONE = 0,
  /** Search from this field. */
  SEARCH = 1,
  /** Set the value of this field. */
  POPULATE = 2,
  /** Do not overwrite. */
  PRESERVE = 4,
  /** Show just the country list. */
  COUNTRY = 8,
}

interface PcaFieldMapping {
  /** The id, or name attribute, of your field */
  element: string;
  /** The name of the address element that should be put into this field */
  field: string;
  /** How this field should be used */
  mode?: PcaFieldMode;
}

interface PcaOptions {
  key: string;
}

interface Events {
  populate: (address: PcaRetrieveResult) => void;
}

type EventKeys = keyof Events;

class AddressControl {
  load() {}

  listen<K extends EventKeys>(event: K, callback: Events[K]) {}

  constructor(fields: PcaFieldMapping[], options: PcaOptions) {}
}

export interface PcaRetrieveResult {
  Id: string;
  DomesticId: string;
  Language: string;
  LanguageAlternatives: string;
  Department: string;
  Company: string;
  SubBuilding: string;
  BuildingNumber: string;
  BuildingName: string;
  SecondaryStreet: string;
  Street: string;
  Block: string;
  Neighbourhood: string;
  District: string;
  City: string;
  Line1: string;
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  AdminAreaName: string;
  AdminAreaCode: string;
  Province: string;
  ProvinceName: string;
  ProvinceCode: string;
  PostalCode: string;
  CountryName: string;
  CountryIso2: string;
  CountryIso3: string;
  CountryIsoNumber: string;
  SortingNumber1: string;
  SortingNumber2: string;
  Barcode: string;
  POBoxNumber: string;
  Label: string;
  Type: string;
  DataLevel: string;
}

interface pca {
  Address: typeof AddressControl;
}

declare var pca: pca;

export class GeocodedPcaAddress {
  public Company: string;
  public Line1: string;
  public Line2: string;
  public Line3: string;
  public City: string;
  public PostalCode: string;
  public Latitude: number;
  public Longitude: number;
  public CountryName: string;
  public Primary: string;
}

type PcaRetrieveResultConverter<TOutType> = (result: PcaRetrieveResult) => Promise<TOutType>;

@Injectable({ providedIn: 'root' })
export class PcaService {
  private pcaKey: string;

  constructor(
    private mapService: MapService,
    private vehicleAddressService: VehicleAddressService,
  ) {}

  public Init(): Promise<boolean> {
    return this.mapService.GetPCAKey().then((result) => {
      if (result) {
        this.pcaKey = result.key;
        return true;
      }

      return false;
    });
  }

  Use<T>(
    fields: PcaFieldMapping[],
    converter: PcaRetrieveResultConverter<T>,
    onAddressChosen?: () => void,
  ): Subject<T> {
    onAddressChosen = onAddressChosen || (() => {});
    var subject = new Subject<T>();
    setTimeout(() => {
      try {
        var control = new pca.Address(fields, { key: this.pcaKey });
        control.load();
        control.listen('populate', async (address) => {
          onAddressChosen();
          try {
            let converted = await converter(address);
            subject.next(converted);
          } catch (err) {
            subject.next(null);
          }
        });
      } catch (err) {}
    }, 0);

    return subject;
  }

  UseWithGeocodedAddress(
    fields: PcaFieldMapping[],
    onAddressChosen?: () => void,
  ): Subject<GeocodedPcaAddress> {
    return this.Use(fields, this.GeocodedAddressConverter, onAddressChosen);
  }

  UseWithQuoteAddressConversion(
    fields: PcaFieldMapping[],
    onAddressChosen?: () => void,
  ): Subject<QuoteAddressDTO> {
    return this.Use(fields, this.QuoteAddressConverter, onAddressChosen);
  }

  UseWithVehicleAddressConversion(
    fields: PcaFieldMapping[],
    onAddressChosen?: () => void,
  ): Subject<IVehicleAddress> {
    return this.Use(fields, this.VehicleAddressConverter, onAddressChosen);
  }

  private GeocodedAddressConverter: PcaRetrieveResultConverter<any> = async (result) => {
    let output = new GeocodedPcaAddress();

    let addressValues = [];
    if (result.Line1) {
      addressValues.push(result.Line1);
    }

    if (result.Line2) {
      addressValues.push(result.Line2);
    }

    if (result.Line3) {
      addressValues.push(result.Line3);
    }

    if (result.City) {
      addressValues.push(result.City);
    }

    if (result.PostalCode) {
      addressValues.push(result.PostalCode);
    }

    let x = await this.mapService.GetAddress(addressValues.join(', '));
    if (!x) {
      return null;
    }
    output.Primary = addressValues.join(', ');
    output.Latitude = x.lat;
    output.Longitude = x.lng;

    if (x.street_number == null && x.route == null)
      output.Line1 = x.formatted_address.toString().split(',')[0];
    else output.Line1 = x.street_number ? x.street_number + ' ' + x.route : x.route;
    output.Line2 = x.locality;
    output.PostalCode = x.postal_code;
    output.Longitude = x.lng;
    output.Latitude = x.lat;
    output.Company = result.Company;
    output.Line3 = '';
    output.City = x.postal_town;
    return output;
  };

  private QuoteAddressConverter: PcaRetrieveResultConverter<QuoteAddressDTO> = async (result) => {
    let output: QuoteAddressDTO = {};

    console.log('RESULT ', result);

    let addressValues = [];

    if (result.Line1) {
      addressValues.push(result.Line1);
    }

    if (result.Line2) {
      addressValues.push(result.Line2);
    }

    if (result.Line3) {
      addressValues.push(result.Line3);
    }

    if (result.City) {
      addressValues.push(result.City);
    }

    if (result.PostalCode) {
      addressValues.push(result.PostalCode);
    }

    let x = await this.mapService.GetAddress(addressValues.join(', '));

    if (!x) {
      return null;
    }

    output.latitude = x.lat;
    output.longitude = x.lng;

    const line1 = [];

    if (result.BuildingNumber && result.BuildingNumber.length > 0) {
      line1.push(result.BuildingNumber);
    }

    if (result.BuildingName && result.BuildingName.length > 0) {
      line1.push(result.BuildingName);
    }

    if (result.Street && result.Street.length > 0) {
      line1.push(result.Street);
    }

    // output.route = x.route;
    output.primaryAddress = addressValues.join(', ');
    output.businessName = result.Company;
    output.addressLine1 = line1.join(', ');
    output.addressLine2 = result.District;
    output.town = result.City;
    output.city = result.Province.length > 0 ? result.Province : result.AdminAreaName;
    output.postcode = result.PostalCode;
    return output;
  };

  private VehicleAddressConverter: PcaRetrieveResultConverter<IVehicleAddress> = async (result) => {
    let x = await this.vehicleAddressService.GetFromPcaAddress(result);
    return x;
  };
}
